import { useOutletContext } from 'react-router-dom'
import DistributionSettings from '@/pages/program_purse_settings/purse-details/_components/distribution-settings.tsx'
import GradeLevelRuleDetails from '@/pages/program_purse_settings/purse-details/_components/distribution-settings-options/details/GradeLevelRuleDetails.tsx'
import EducationTypeRuleDetails from '@/pages/program_purse_settings/purse-details/_components/distribution-settings-options/details/EducationTypeRuleDetails.tsx'
import FixedAmountRuleDetails from '@/pages/program_purse_settings/purse-details/_components/distribution-settings-options/details/FixedAmountRuleDetails.tsx'
import RuleSuccess from '@/pages/program_purse_settings/purse-details/_components/distribution-settings-options/rule-success.tsx'
import { useDistributionSettingsWizard } from '@/pages/program_purse_settings/purse-details/_components/distribution-settings-options/purse-detail-view/useDistributionSettingsWizard.ts'
import {
  ModifierCode,
  ProgramPurseResource,
} from '@/queries/program_purses/schemas.ts'
import SpecialNeedsModifierView from '../shared/SpecialNeedsModifierView'
import NonGradeModifierView from '../shared/NonGradeModifierView'
import {
  groupModifiersByCode,
  getNonGradeBasedModifiers,
} from '../modifiers/utils'

interface OutletContext {
  purse: ProgramPurseResource
}

export function DistributionSettingsTabLoader() {
  const { purse } = useOutletContext<OutletContext>()
  const wizardController = useDistributionSettingsWizard({
    initialRuleType: purse.awarding_method_code,
  })

  const modifiersByCode = groupModifiersByCode(
    purse.distributions_rules.distribution_modifiers
  )
  const nonGradeBasedModifiers = getNonGradeBasedModifiers(modifiersByCode)

  const extraModifierData = Object.entries(nonGradeBasedModifiers).map(
    ([code, group]) => ({
      code: code as ModifierCode,
      modifierName: group[0].modifier_name,
      isActive: group[0].is_active,
    })
  )

  const renderConfigureRuleStep = () => {
    const commonConfigureProps = {
      variant: 'form_view' as const,
      onBack: wizardController.handleBackToChoose,
      onSuccessfulSave: wizardController.handleRuleComplete,
      onNavigateToSpecialNeedsModifier:
        wizardController.handleNavigateToSpecialNeedsModifier,
    }

    const purseProp = {
      id: purse.id,
      programId: purse.program_id,
      canDistributeAutomatically: purse.can_distribute_funds_automatically,
      affiliationRestrictionEnabled: purse.affiliation_restriction_enabled,
      bankAccountId: purse.bank_account_id,
    }

    switch (wizardController.state.selectedRuleType) {
      case 'grade_level':
        return (
          <GradeLevelRuleDetails
            {...commonConfigureProps}
            partialPurseData={purseProp}
            gradeDistributionRules={
              purse.distributions_rules.grade_distributions
            }
          />
        )

      case 'education_type':
        return (
          <EducationTypeRuleDetails
            {...commonConfigureProps}
            partialPurseData={purseProp}
            educationDistributionRules={
              purse.distributions_rules.education_distributions
            }
          />
        )

      case 'fixed_amount':
        return (
          <FixedAmountRuleDetails
            {...commonConfigureProps}
            partialPurseData={purseProp}
            fixedAmountRule={purse.distributions_rules.fixed_distribution}
          />
        )

      default:
        // Fallback - shouldn't happen with proper state management
        return (
          <DistributionSettings
            onCancel={() => {}}
            onContinue={wizardController.handleRuleSelection}
          />
        )
    }
  }

  const renderViewAllSettingsStep = () => {
    const commonViewProps = {
      variant: 'all_settings_view' as const,
      onChangeRule: wizardController.handleChangeRule,
      onSuccessfulSave: () => {},
      onNavigateToSpecialNeedsModifier:
        wizardController.handleNavigateToSpecialNeedsModifier,
    }

    const purseProp = {
      id: purse.id,
      programId: purse.program_id,
      canDistributeAutomatically: purse.can_distribute_funds_automatically,
      affiliationRestrictionEnabled: purse.affiliation_restriction_enabled,
      bankAccountId: purse.bank_account_id,
    }

    switch (wizardController.state.completedRuleType) {
      case 'grade_level':
        return (
          <GradeLevelRuleDetails
            {...commonViewProps}
            partialPurseData={purseProp}
            gradeDistributionRules={
              purse.distributions_rules.grade_distributions
            }
            extraModifierData={extraModifierData}
            onConfigureModifier={
              wizardController.handleNavigateToNonGradeModifier
            }
          />
        )

      case 'education_type':
        return (
          <EducationTypeRuleDetails
            {...commonViewProps}
            partialPurseData={purseProp}
            educationDistributionRules={
              purse.distributions_rules.education_distributions
            }
            extraModifierData={extraModifierData}
            onConfigureModifier={
              wizardController.handleNavigateToNonGradeModifier
            }
          />
        )

      case 'fixed_amount':
        return (
          <FixedAmountRuleDetails
            {...commonViewProps}
            partialPurseData={purseProp}
            fixedAmountRule={purse.distributions_rules.fixed_distribution}
            extraModifierData={extraModifierData}
            onConfigureModifier={
              wizardController.handleNavigateToNonGradeModifier
            }
          />
        )

      default:
        // Fallback if no completed rule type
        return (
          <DistributionSettings
            onCancel={() => {}}
            onContinue={wizardController.handleRuleSelection}
          />
        )
    }
  }

  if (wizardController.state.currentStep == 'choose_award_rule') {
    return (
      <DistributionSettings
        onCancel={() => {}}
        onContinue={wizardController.handleRuleSelection}
      />
    )
  } else if (wizardController.state.currentStep == 'configure_rule') {
    return renderConfigureRuleStep()
  } else if (wizardController.state.currentStep == 'success') {
    return <RuleSuccess onViewRuleClick={wizardController.handleViewRule} />
  } else if (wizardController.state.currentStep == 'view_purse_settings') {
    return renderViewAllSettingsStep()
  } else if (wizardController.state.currentStep === 'special_needs_modifier') {
    const purseProp = {
      id: purse.id,
      programId: purse.program_id,
      canDistributeAutomatically: purse.can_distribute_funds_automatically,
      affiliationRestrictionEnabled: purse.affiliation_restriction_enabled,
      bankAccountId: purse.bank_account_id,
    }
    return (
      <SpecialNeedsModifierView
        partialPurseData={purseProp}
        distributionModifiers={purse.distributions_rules.distribution_modifiers}
        onBack={wizardController.handleViewRule}
      />
    )
  } else if (wizardController.state.currentStep === 'modifier') {
    const code = wizardController.state.selectedModifierCode
    const group = code ? (modifiersByCode[code] ?? []) : []

    if (!code || group.length === 0) {
      return (
        <DistributionSettings
          onCancel={() => {}}
          onContinue={wizardController.handleRuleSelection}
        />
      )
    }

    const purseProp = {
      id: purse.id,
      programId: purse.program_id,
      canDistributeAutomatically: purse.can_distribute_funds_automatically,
      affiliationRestrictionEnabled: purse.affiliation_restriction_enabled,
      bankAccountId: purse.bank_account_id,
    }

    return (
      <NonGradeModifierView
        modifierId={group[0].id}
        modifierTypeId={group[0].modifier_type_id}
        modifierCode={code}
        modifierName={group[0].modifier_name}
        baseAmount={group[0].base_amount}
        isActive={group[0].is_active}
        partialPurseData={purseProp}
        onBack={wizardController.handleViewRule}
      />
    )
  } else {
    return (
      <DistributionSettings
        onCancel={() => {}}
        onContinue={wizardController.handleRuleSelection}
      />
    )
  }
}
